import React, { useEffect } from "react";
import { Grid, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AuthViewController from "../redux/actions/AuthViewController";
import "../styles/layout.css";
import "../styles/dashboard.css";

const pct = (num, denom) =>
  denom ? `${((num / denom) * 100).toFixed(0)}%` : "0%";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getAdminDashboardData } = AuthViewController();
  const { admindashboardData: data, loading, error } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    dispatch(getAdminDashboardData());
  }, [dispatch]);

  if (loading && !data) {
    return (
      <div className="dashboard-loading">
        <CircularProgress size={32} sx={{ color: "var(--brand-orange)" }} />
      </div>
    );
  }

  const growth = data?.growth || {};

  return (
    <div className="dashboard-page admin-dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Platform overview</p>
      </div>

      {error && !data && (
        <div className="dashboard-error-banner">
          Unable to load dashboard data. Please refresh the page or log in again
          if the problem persists.
        </div>
      )}

      <div className="admin-quick-actions">
        <button
          type="button"
          className="admin-quick-action-btn primary"
          onClick={() => navigate("/company")}
        >
          <AddBusinessOutlinedIcon />
          Add Company
        </button>
        <button
          type="button"
          className="admin-quick-action-btn"
          onClick={() => navigate("/employer")}
        >
          <ManageAccountsOutlinedIcon />
          View Employers
        </button>
        <button
          type="button"
          className="admin-quick-action-btn"
          onClick={() => navigate("/reports")}
        >
          <AssessmentOutlinedIcon />
          Reports
        </button>
      </div>

      <div className="dashboard-stats list-page-stats">
        <Grid container spacing={1}>
          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/company")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-primary">
                <BusinessOutlinedIcon className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Companies</h6>
                <h3 className="value">
                  {data?.active_companies || 0} / {data?.total_companies || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(data?.active_companies, data?.total_companies)}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/employer")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-info">
                <GroupIcon className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Employers</h6>
                <h3 className="value">
                  {data?.total_active_employer || 0} /{" "}
                  {data?.total_employer || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(data?.total_active_employer, data?.total_employer)}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/users")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-primary">
                <PersonAddOutlinedIcon className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Employees</h6>
                <h3 className="value">
                  {data?.total_active_employee || 0} /{" "}
                  {data?.total_employee || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(data?.total_active_employee, data?.total_employee)}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/leaves")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-danger">
                <PendingActionsIcon className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Pending Activations</h6>
                <h3 className="value">
                  {data?.pending_activations || 0}
                  {(data?.pending_leave_requests || 0) > 0 && (
                    <span className="fs-12 fw-medium text-muted ms-2">
                      · {data.pending_leave_requests} leaves
                    </span>
                  )}
                </h3>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      <div className="dashboard-section-grid">
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h5 className="dashboard-card-title">Companies Overview</h5>
            <p className="dashboard-card-subtitle">
              Recent organizations on the platform
            </p>
          </div>

          {data?.recent_companies?.length > 0 ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Location</th>
                    <th>Employees</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_companies.map((company) => (
                    <tr key={company.id}>
                      <td>{company.company_name}</td>
                      <td>{company.location}</td>
                      <td>{company.employee_count}</td>
                      <td>
                        <span
                          className={`admin-status-badge ${
                            company.status === "Active" ? "active" : "inactive"
                          }`}
                        >
                          {company.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="dashboard-empty">
              <BusinessOutlinedIcon className="dashboard-empty-icon" />
              <p>No companies yet</p>
              <p className="dashboard-empty-hint">
                Add your first company to get started
              </p>
            </div>
          )}

          <Link to="/company" className="dashboard-action-btn">
            View All Companies
          </Link>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h5 className="dashboard-card-title">Platform Growth</h5>
            <p className="dashboard-card-subtitle">New signups this month</p>
          </div>

          <div className="admin-growth-grid">
            <div className="admin-growth-item">
              <TrendingUpOutlinedIcon className="admin-growth-icon" />
              <p className="admin-growth-label">New Companies</p>
              <h3>{growth.new_companies_this_month || 0}</h3>
            </div>
            <div className="admin-growth-item">
              <TrendingUpOutlinedIcon className="admin-growth-icon" />
              <p className="admin-growth-label">New Employers</p>
              <h3>{growth.new_employers_this_month || 0}</h3>
            </div>
            <div className="admin-growth-item">
              <TrendingUpOutlinedIcon className="admin-growth-icon" />
              <p className="admin-growth-label">New Employees</p>
              <h3>{growth.new_employees_this_month || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-bottom-grid">
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h5 className="dashboard-card-title">Recent Activity</h5>
            <p className="dashboard-card-subtitle">
              Latest employee signups and pending leave requests
            </p>
          </div>

          <h6 className="admin-activity-heading">Recent Employee Signups</h6>
          {data?.recent_employees?.length > 0 ? (
            <div className="admin-activity-list">
              {data.recent_employees.map((emp, index) => (
                <div className="admin-activity-item" key={`emp-${index}`}>
                  <div>
                    <p className="admin-activity-title">{emp.name}</p>
                    <p className="admin-activity-meta">{emp.company_name}</p>
                  </div>
                  <div className="admin-activity-right">
                    <span
                      className={`admin-status-badge ${
                        emp.is_active ? "active" : "inactive"
                      }`}
                    >
                      {emp.is_active ? "Active" : "Pending"}
                    </span>
                    <span className="admin-activity-date">
                      {formatDate(emp.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dashboard-empty compact">
              <PersonAddOutlinedIcon className="dashboard-empty-icon" />
              <p>No recent employee signups</p>
            </div>
          )}

          <h6 className="admin-activity-heading">Pending Leave Requests</h6>
          {data?.recent_pending_leaves?.length > 0 ? (
            <div className="admin-activity-list">
              {data.recent_pending_leaves.map((leave, index) => (
                <div className="admin-activity-item" key={`leave-${index}`}>
                  <div>
                    <p className="admin-activity-title">{leave.employee_name}</p>
                    <p className="admin-activity-meta">{leave.company_name}</p>
                  </div>
                  <div className="admin-activity-right">
                    <span className="admin-activity-date">
                      From {formatDate(leave.leave_start_date)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dashboard-empty compact">
              <PendingActionsIcon className="dashboard-empty-icon" />
              <p>No pending leave requests</p>
            </div>
          )}

          <Link to="/leaves" className="dashboard-action-btn">
            View All Leave Requests
          </Link>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h5 className="dashboard-card-title">Subscriptions &amp; System</h5>
            <p className="dashboard-card-subtitle">
              Plans and platform announcements
            </p>
          </div>

          <div className="admin-system-grid">
            <div className="admin-system-item">
              <CardMembershipOutlinedIcon className="admin-system-icon" />
              <div>
                <p className="admin-system-label">Subscription Plans</p>
                <h3>{data?.subscription_plans_count || 0}</h3>
              </div>
            </div>
            <div className="admin-system-item">
              <CampaignOutlinedIcon className="admin-system-icon" />
              <div>
                <p className="admin-system-label">Active Announcements</p>
                <h3>{data?.active_announcements_count || 0}</h3>
              </div>
            </div>
          </div>

          <div className="admin-system-links">
            <Link to="/plans" className="dashboard-action-btn">
              Manage Subscription Plans
            </Link>
            <Link to="/announcement" className="dashboard-action-btn">
              View Announcements
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
